import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import { X, Trash2, Edit2, FolderOpen } from 'lucide-react';
import { Analysis, AnalysisUpdate, analysisAPI, Project, projectAPI } from '@/config';

interface AnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (analysis: Analysis) => void;
  analysis: Analysis | null;
  mode: 'rename' | 'move' | 'delete';
}

export const AnalysisModal: React.FC<AnalysisModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  analysis,
  mode
}) => {
  const [campaignName, setCampaignName] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load projects when modal opens for move operation
  useEffect(() => {
    if (isOpen && mode === 'move') {
      const fetchProjects = async () => {
        try {
          const projectsData = await projectAPI.list();
          setProjects(projectsData);
        } catch (error) {
          console.error('Error fetching projects:', error);
          setError('Failed to load projects');
        }
      };
      fetchProjects();
    }
  }, [isOpen, mode]);

  // Initialize form data when analysis changes
  useEffect(() => {
    if (analysis) {
      setCampaignName(analysis.campaignName || '');
      setSelectedProjectId(analysis.project_id || null);
    }
  }, [analysis]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCampaignName('');
      setSelectedProjectId(null);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!analysis) return;

    setIsLoading(true);
    setError(null);

    try {
      if (mode === 'delete') {
        await analysisAPI.delete(analysis.unique_id);
        onSuccess(analysis); // Pass the deleted analysis for removal from state
      } else {
        const updateData: AnalysisUpdate = {};
        
        if (mode === 'rename') {
          if (!campaignName.trim()) {
            setError('Campaign name is required');
            setIsLoading(false);
            return;
          }
          updateData.campaign_name = campaignName.trim();
        } else if (mode === 'move') {
          // Use 0 to represent "unassigned" (no project)
          updateData.project_id = selectedProjectId || 0;
        }

        const updatedAnalysis = await analysisAPI.update(analysis.unique_id, updateData);
        onSuccess(updatedAnalysis);
      }
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const getModalTitle = () => {
    switch (mode) {
      case 'rename': return 'Rename Compliance Check';
      case 'move': return 'Move Compliance Check';
      case 'delete': return 'Delete Compliance Check';
      default: return 'Analysis';
    }
  };

  const getModalIcon = () => {
    switch (mode) {
      case 'rename': return <Edit2 className="w-5 h-5" />;
      case 'move': return <FolderOpen className="w-5 h-5" />;
      case 'delete': return <Trash2 className="w-5 h-5" />;
      default: return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {getModalIcon()}
            <h2 className="text-sm font-semibold">{getModalTitle()}</h2>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="p-1"
          >
            <X size={16} />
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          {mode === 'rename' && (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Campaign Name
              </label>
              <Input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Enter campaign name"
                disabled={isLoading}
                required
              />
            </div>
          )}

          {mode === 'move' && (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Move to Project
              </label>
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                disabled={isLoading}
              >
                <option value="">Unassigned</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mode === 'delete' && (
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete the analysis "{analysis?.campaignName}"? 
                This action cannot be undone and will permanently delete all associated files.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className={mode === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
              style={mode !== 'delete' ? { 
                background: '#B1FA63', 
                color: '#243837',
                border: 'none'
              } : {}}
            >
              {isLoading ? 'Processing...' : 
               mode === 'delete' ? 'Delete' : 
               mode === 'rename' ? 'Save' : 'Move'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnalysisModal; 